/**
 * Native Firebase notification lifecycle.
 *
 * One device token serves every kind of push the app receives — specialist chat
 * replies and webinar reminders — because FCM addresses the installation, not the
 * feature. Permission is therefore asked for once, at whichever high-intent moment
 * comes first, and the payload's own `type` decides what a tap does.
 *
 * Listeners are installed at shell start so token refreshes are persisted and a tap
 * can open its destination even when it launched the app from a cold state.
 *
 * The API side of this is still called `chat_devices` (`PUT /api/v1/chat/devices`).
 * Renaming it would cost a migration and orphan every installed bundle, which ships
 * its own `dist/` and keeps calling the old path regardless.
 */

import type { PluginListenerHandle } from "@capacitor/core";

import { registerChatDevice, unregisterChatDevice } from "~/lib/api";
import { isNative, platform } from "~/lib/capacitor";
import type { NotificationKind } from "~/lib/notification-routes";
import {
  notificationKind,
  routeFromNotificationData,
} from "~/lib/notification-routes";
import {
  PENDING_ROUTE_EVENT,
  setPendingRoute,
  takePendingRoute,
} from "~/lib/pending-route";
import { getItem, removeItem, setItem, StorageKey } from "~/lib/storage";
import type { ChatDevicePlatform } from "~/types/chat";

/**
 * Android channel ids. Frozen: a channel id is the key the OS stores the member's own
 * per-channel preferences under, and the server addresses these exact strings
 * (`AndroidNotification(channel_id=...)`). Renaming one silently moves every upgraded
 * install onto a fresh channel and loses whatever they had configured.
 */
const CHAT_CHANNEL_ID = "chat_messages";
const EVENT_REMINDER_CHANNEL_ID = "event_reminders";

export type NotificationPermissionState =
  "granted" | "denied" | "prompt" | "unsupported";

export interface NotificationChannelCopy {
  name: string;
  description: string;
}

/**
 * Localised channel copy. Optional throughout: the channels also have to be creatable
 * from paths that have no `t()` in hand, and an English default in the OS settings
 * list beats no channel at all.
 */
export interface NotificationChannelCopyMap {
  chat?: NotificationChannelCopy;
  eventReminders?: NotificationChannelCopy;
}

interface PushNotificationCallbacks {
  /** A tap resolved to a route. Called on mount for a route parked before then. */
  onOpenRoute: (route: string) => void;
  /** A notification arrived while the app was in the foreground. */
  onNotification: (kind: NotificationKind) => void;
}

interface StoredChatDeviceRegistration {
  token: string;
  owner_user_id: string;
  rotation_pending: boolean;
}

let activeOwnerUserId: string | null = null;
let registrationPromise: Promise<void> | null = null;
let registrationQueue: Promise<void> = Promise.resolve();

function devicePlatform(): ChatDevicePlatform | null {
  const current = platform();
  return current === "ios" || current === "android" ? current : null;
}

interface ChannelDefinition {
  id: string;
  key: keyof NotificationChannelCopyMap;
  /** Shown only where no call site could supply the member's own language. */
  fallback: NotificationChannelCopy;
  visibility: 0 | 1;
}

/** Declaration order is the order Android lists them in the app's settings. */
const CHANNEL_DEFINITIONS: readonly ChannelDefinition[] = [
  {
    id: CHAT_CHANNEL_ID,
    key: "chat",
    fallback: {
      name: "Chat messages",
      description: "Replies from the Elyxeer team",
    },
    // Treat a personal health/wellness conversation as private on the lock
    // screen even though the notification copy itself is deliberately generic.
    visibility: 0,
  },
  {
    id: EVENT_REMINDER_CHANNEL_ID,
    key: "eventReminders",
    fallback: {
      name: "Webinars and events",
      description: "Reminders before a live session starts",
    },
    // Public, unlike chat: the title of a webinar everyone was invited to is not a
    // secret, and hiding it would make the reminder useless from the lock screen.
    visibility: 1,
  },
];

async function existingChannelIds(messaging: {
  listChannels: () => Promise<{ channels: { id: string }[] }>;
}): Promise<string[] | null> {
  try {
    const { channels } = await messaging.listChannels();
    return channels.map((channel) => channel.id);
  } catch {
    // Nothing to weigh with, so fall through to creating: a channel named in English
    // is cosmetic, whereas a missing one silently files every notification under
    // Android's "Miscellaneous" with no error anywhere the server could see.
    return null;
  }
}

/**
 * Create every channel the app pushes on, in one pass.
 *
 * Two different callers need this and only one of them has a `t()` in hand.
 * `createChannel` is an upsert over a channel's *name and description*, so an
 * unconditional pass from the caller without copy would overwrite the localised names
 * a member already sees with the English fallbacks above — on every launch, and
 * permanently until they reinstall. A channel is therefore re-asserted only when this
 * call can name it in the member's own language, and otherwise created only when it is
 * absent. That still closes the upgrade hole the second call site exists for: a member
 * who granted permission through chat in an earlier release has no `event_reminders`
 * channel at all, and it gets created here on their next hydrate.
 */
async function createNotificationChannels(
  copy?: NotificationChannelCopyMap,
): Promise<void> {
  if (devicePlatform() !== "android") return;
  const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");

  const existing = CHANNEL_DEFINITIONS.every(
    (definition) => copy?.[definition.key],
  )
    ? null
    : await existingChannelIds(FirebaseMessaging);

  for (const definition of CHANNEL_DEFINITIONS) {
    const localised = copy?.[definition.key];
    if (!localised && existing?.includes(definition.id)) continue;
    await FirebaseMessaging.createChannel({
      id: definition.id,
      name: localised?.name ?? definition.fallback.name,
      description: localised?.description ?? definition.fallback.description,
      importance: 4,
      visibility: definition.visibility,
      vibration: true,
    });
  }
}

async function readStoredRegistration(): Promise<StoredChatDeviceRegistration | null> {
  const raw = await getItem(StorageKey.ChatDeviceRegistration);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredChatDeviceRegistration>;
    return typeof parsed.token === "string" &&
      typeof parsed.owner_user_id === "string"
      ? {
          token: parsed.token,
          owner_user_id: parsed.owner_user_id,
          rotation_pending: parsed.rotation_pending === true,
        }
      : null;
  } catch {
    return null;
  }
}

async function writeStoredRegistration(
  registration: StoredChatDeviceRegistration,
): Promise<void> {
  await setItem(
    StorageKey.ChatDeviceRegistration,
    JSON.stringify(registration),
  );
}

async function retryPendingRotation(messaging: {
  deleteToken: () => Promise<void>;
}): Promise<void> {
  const stored = await readStoredRegistration();
  if (!stored?.rotation_pending) return;
  try {
    await messaging.deleteToken();
    await removeItem(StorageKey.ChatDeviceRegistration);
  } catch {
    // Keep the marker and old token for the next native start/account activation.
  }
}

function registerTokenForOwner(
  token: string,
  ownerUserId: string,
): Promise<void> {
  const currentPlatform = devicePlatform();
  if (!currentPlatform) return Promise.resolve();

  // Serialize ownership changes. If account A's PUT is already in flight when the
  // device switches to B, B's PUT runs second and therefore becomes authoritative.
  const task = registrationQueue
    .catch(() => undefined)
    .then(async () => {
      if (activeOwnerUserId !== ownerUserId) return;
      await registerChatDevice(token, currentPlatform);
      // Record exactly what the API now owns. Logout can detach this token without
      // consulting OS permission (which may have been revoked since registration).
      await writeStoredRegistration({
        token,
        owner_user_id: ownerUserId,
        rotation_pending: false,
      });
    });
  registrationQueue = task.catch(() => undefined);
  return task;
}

async function persistRefreshedToken(token: string): Promise<void> {
  const ownerUserId = activeOwnerUserId;
  if (!ownerUserId) return;
  const stored = await readStoredRegistration();
  // A token event alone is not consent. Only refresh a token for the authenticated
  // account that already owns an explicitly registered chat device.
  if (
    activeOwnerUserId !== ownerUserId ||
    stored?.owner_user_id !== ownerUserId ||
    stored.rotation_pending
  )
    return;
  await registerTokenForOwner(token, ownerUserId);
}

/** Install native listeners without prompting for notification permission. */
export async function initializePushNotifications({
  onOpenRoute,
  onNotification,
}: PushNotificationCallbacks): Promise<() => void> {
  if (!isNative() || !devicePlatform()) return () => undefined;

  const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
  await retryPendingRotation(FirebaseMessaging);
  const handles: PluginListenerHandle[] = [];

  handles.push(
    await FirebaseMessaging.addListener("tokenReceived", ({ token }) => {
      // A refresh should never interrupt the member. The next chat open retries if
      // the API happens to be offline at this moment.
      void persistRefreshedToken(token).catch(() => undefined);
    }),
    await FirebaseMessaging.addListener(
      "notificationReceived",
      ({ notification }) => {
        const kind = notificationKind(notification.data);
        if (kind) onNotification(kind);
      },
    ),
    await FirebaseMessaging.addListener(
      "notificationActionPerformed",
      ({ notification }) => {
        // Parked rather than opened: a launch tap arrives while the auth store is
        // still hydrating, and the consumer below replays it once routing can stick.
        const route = routeFromNotificationData(notification.data);
        if (route) setPendingRoute(route);
      },
    ),
  );

  const drain = () => {
    const route = takePendingRoute();
    if (route) onOpenRoute(route);
  };
  // Subscribe before draining: a tap landing between the two is signalled by the
  // event, and `takePendingRoute` makes the double delivery a no-op either way.
  window.addEventListener(PENDING_ROUTE_EVENT, drain);
  drain();

  return () => {
    window.removeEventListener(PENDING_ROUTE_EVENT, drain);
    for (const handle of handles) void handle.remove();
  };
}

/**
 * Bind notification ownership to an authenticated member without showing a prompt.
 * If this device has already granted OS permission, login immediately transfers the
 * FCM token to the new account instead of waiting for that member to open chat.
 */
export async function activatePushNotifications(userId: string): Promise<void> {
  activeOwnerUserId = userId;
  if (!isNative() || !devicePlatform()) return;

  const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
  await retryPendingRotation(FirebaseMessaging);
  const supported = await FirebaseMessaging.isSupported();
  if (!supported.isSupported) return;
  const permission = await FirebaseMessaging.checkPermissions();
  if (permission.receive !== "granted" || activeOwnerUserId !== userId) return;
  // Every hydrate and verify passes here, which is the only moment an install that
  // granted permission in an older release can acquire the channels it now needs.
  // No copy to pass: this path has no `t()`, so it only fills in what is missing and
  // leaves the names an already-created channel carries alone.
  await createNotificationChannels();

  const { token } = await FirebaseMessaging.getToken();
  if (activeOwnerUserId !== userId) return;
  await registerTokenForOwner(token, userId);
}

/**
 * Ask once, at a moment the member has shown intent, then persist the cross-platform
 * FCM token. iOS grants exactly one system dialog per install, so this is called from
 * entering chat, from the notification centre's own card and from the notification
 * setting — never at boot. A denied permission remains a quiet no-op.
 */
export async function ensureNotificationPermission(options?: {
  userId: string;
  channels?: NotificationChannelCopyMap;
}): Promise<void> {
  if (!isNative() || !devicePlatform()) return;
  const userId = options?.userId;
  if (!userId) return;
  activeOwnerUserId = userId;
  if (registrationPromise) return registrationPromise;

  registrationPromise = (async () => {
    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
    await retryPendingRotation(FirebaseMessaging);
    const supported = await FirebaseMessaging.isSupported();
    if (!supported.isSupported) return;

    let permission = await FirebaseMessaging.checkPermissions();
    if (
      permission.receive === "prompt" ||
      permission.receive === "prompt-with-rationale"
    ) {
      permission = await FirebaseMessaging.requestPermissions();
    }
    if (permission.receive !== "granted") return;
    if (activeOwnerUserId !== userId) return;

    await createNotificationChannels(options?.channels);

    const { token } = await FirebaseMessaging.getToken();
    if (activeOwnerUserId !== userId) return;
    await registerTokenForOwner(token, userId);
  })().finally(() => {
    registrationPromise = null;
  });

  return registrationPromise;
}

/**
 * What the OS currently says, flattened for the UI.
 *
 * Both of Capacitor's prompt states collapse to `"prompt"` — the rationale variant is
 * an Android detail nothing in this app renders differently — and anything that is not
 * a notification-capable native install is `"unsupported"`, which is what the settings
 * screen shows instead of a switch that could never do anything.
 */
export async function notificationPermissionState(): Promise<NotificationPermissionState> {
  if (!isNative() || !devicePlatform()) return "unsupported";

  const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
  const supported = await FirebaseMessaging.isSupported();
  if (!supported.isSupported) return "unsupported";

  const { receive } = await FirebaseMessaging.checkPermissions();
  if (receive === "granted") return "granted";
  if (receive === "denied") return "denied";
  return "prompt";
}

/** Detach this device token before the authenticated session is discarded. */
export async function disablePushNotifications(): Promise<void> {
  const departingUserId = activeOwnerUserId;
  // Gate tokenReceived immediately; Firebase may emit while the remaining cleanup
  // awaits the network or rotates a token after a failed server detach.
  activeOwnerUserId = null;
  if (!isNative() || !devicePlatform()) return;

  await registrationPromise?.catch(() => undefined);
  await registrationQueue.catch(() => undefined);
  const stored = await readStoredRegistration();
  if (!stored) return;

  let detached = false;
  if (departingUserId && stored.owner_user_id === departingUserId) {
    try {
      await unregisterChatDevice(stored.token);
      detached = true;
    } catch {
      // Rotating below invalidates the server's stale FCM target when the API is
      // offline. Logout itself still proceeds regardless of either cleanup result.
    }
  }
  if (detached) {
    await removeItem(StorageKey.ChatDeviceRegistration);
  } else {
    await writeStoredRegistration({ ...stored, rotation_pending: true });
    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
    await retryPendingRotation(FirebaseMessaging);
  }
}

/**
 * Drop local delivery when the session is already invalid and an authenticated
 * server detach is impossible. The old backend target becomes unusable after the
 * FCM token is deleted, and refresh events cannot reattach it without an owner.
 */
export async function discardPushNotifications(): Promise<void> {
  activeOwnerUserId = null;
  if (!isNative() || !devicePlatform()) return;

  await registrationPromise?.catch(() => undefined);
  await registrationQueue.catch(() => undefined);
  const stored = await readStoredRegistration();
  if (!stored) return;

  await writeStoredRegistration({ ...stored, rotation_pending: true });
  const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
  await retryPendingRotation(FirebaseMessaging);
}
